import React from 'react';
import ModernCTA from '../../components/ModernCTA';

const AISurveillance = () => {
  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>AI Surveillance Solutions</h1>
          <p style={styles.heroSubtitle}>
            Next-generation smart city security with facial recognition, behavior analysis, and predictive threat detection.
          </p>
        </div>
        <div style={styles.heroVisualization}>
          <div style={styles.cameraArray}>
            <div style={styles.camera}></div>
            <div style={styles.camera}></div>
            <div style={styles.camera}></div>
          </div>
          <div style={styles.dataGrid}></div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section style={styles.capabilities}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTag}>TECHNOLOGY</span>
          <h2 style={styles.sectionTitle}>Advanced Surveillance Capabilities</h2>
          <p style={styles.sectionDescription}>
            Our AI-powered systems provide real-time monitoring and intelligent analysis for urban security.
          </p>
        </div>

        <div style={styles.capabilityGrid}>
          <div style={styles.capabilityCard}>
            <div style={styles.capabilityIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 18a5 5 0 0 0-10 0"></path>
                <line x1="12" y1="2" x2="12" y2="9"></line>
                <line x1="4.22" y1="10.22" x2="5.64" y2="11.64"></line>
                <line x1="1" y1="18" x2="3" y2="18"></line>
                <line x1="21" y1="18" x2="23" y2="18"></line>
                <line x1="18.36" y1="11.64" x2="19.78" y2="10.22"></line>
                <line x1="23" y1="22" x2="1" y2="22"></line>
                <polyline points="16 5 12 9 8 5"></polyline>
              </svg>
            </div>
            <h3 style={styles.capabilityTitle}>Facial Recognition</h3>
            <p style={styles.capabilityText}>
              99.8% accurate identity verification with real-time database matching and watchlist alerts.
            </p>
            <ul style={styles.featureList}>
              <li style={styles.featureListItem}>Multi-angle detection</li>
              <li style={styles.featureListItem}>Partial face recognition</li>
              <li style={styles.featureListItem}>Age/gender estimation</li>
              <li style={styles.featureListItem}>Emotion analysis</li>
            </ul>
          </div>

          <div style={styles.capabilityCard}>
            <div style={styles.capabilityIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <h3 style={styles.capabilityTitle}>Anomaly Detection</h3>
            <p style={styles.capabilityText}>
              AI models trained to identify unusual behavior patterns and potential threats in crowded environments.
            </p>
            <ul style={styles.featureList}>
              <li style={styles.featureListItem}>Crowd behavior analysis</li>
              <li style={styles.featureListItem}>Abandoned object detection</li>
              <li style={styles.featureListItem}>Perimeter breach alerts</li>
              <li style={styles.featureListItem}>Violence detection</li>
            </ul>
          </div>

          <div style={styles.capabilityCard}>
            <div style={styles.capabilityIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a10 10 0 0 0-9 6.5 10 10 0 0 0 18 0A10 10 0 0 0 12 2z"></path>
                <path d="M12 2v20"></path>
                <path d="M2 12h20"></path>
              </svg>
            </div>
            <h3 style={styles.capabilityTitle}>Predictive Policing</h3>
            <p style={styles.capabilityText}>
              Machine learning algorithms that analyze historical data to predict and prevent criminal activity.
            </p>
            <ul style={styles.featureList}>
              <li style={styles.featureListItem}>Crime hotspot prediction</li>
              <li style={styles.featureListItem}>Resource allocation optimization</li>
              <li style={styles.featureListItem}>Pattern recognition</li>
              <li style={styles.featureListItem}>Real-time risk assessment</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section style={styles.integration}>
        <div style={styles.integrationContent}>
          <div style={styles.integrationText}>
            <span style={styles.sectionTag}>SYSTEM INTEGRATION</span>
            <h2 style={styles.sectionTitle}>Smart City Ecosystem</h2>
            <p style={styles.integrationDescription}>
              Our surveillance solutions integrate seamlessly with existing city infrastructure and IoT networks.
            </p>
            <div style={styles.integrationPoints}>
              <div style={styles.integrationPoint}>
                <div style={styles.pointIndicator}></div>
                <div>
                  <h4 style={styles.pointTitle}>Traffic Camera Networks</h4>
                  <p style={styles.pointText}>Leverage existing infrastructure for comprehensive coverage</p>
                </div>
              </div>
              <div style={styles.integrationPoint}>
                <div style={styles.pointIndicator}></div>
                <div>
                  <h4 style={styles.pointTitle}>Emergency Response Systems</h4>
                  <p style={styles.pointText}>Automatic alerts to police, fire, and medical services</p>
                </div>
              </div>
              <div style={styles.integrationPoint}>
                <div style={styles.pointIndicator}></div>
                <div>
                  <h4 style={styles.pointTitle}>Public Safety Databases</h4>
                  <p style={styles.pointText}>Real-time cross-referencing with criminal records</p>
                </div>
              </div>
            </div>
          </div>
          <div style={styles.integrationVisual}>
            <div style={styles.cityGrid}>
              <div style={styles.gridRow}>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCell}></div>
              </div>
              <div style={styles.gridRow}>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCellActive}></div>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCell}></div>
              </div>
              <div style={styles.gridRow}>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCellActive}></div>
                <div style={styles.gridCell}></div>
              </div>
              <div style={styles.gridRow}>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCell}></div>
                <div style={styles.gridCellActive}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section style={styles.caseStudies}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTag}>DEPLOYMENTS</span>
          <h2 style={styles.sectionTitle}>Proven Urban Security Solutions</h2>
        </div>

        <div style={styles.studyGrid}>
          <div style={styles.studyCard}>
            <div style={styles.studyHeader}>
              <div style={styles.studyLogo}></div>
              <h3 style={styles.studyTitle}>Metropolitan Area</h3>
            </div>
            <p style={styles.studyText}>
              Reduced violent crime by 35% in the first year with predictive policing and real-time surveillance.
            </p>
            <div style={styles.studyStats}>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>35%</span>
                <span style={styles.statLabel}>Crime Reduction</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>400+</span>
                <span style={styles.statLabel}>Cameras Deployed</span>
              </div>
            </div>
          </div>

          <div style={styles.studyCard}>
            <div style={styles.studyHeader}>
              <div style={styles.studyLogo}></div>
              <h3 style={styles.studyTitle}>Transportation Hub</h3>
            </div>
            <p style={styles.studyText}>
              Achieved 92% accuracy in threat detection with AI-powered behavior analysis across all terminals.
            </p>
            <div style={styles.studyStats}>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>92%</span>
                <span style={styles.statLabel}>Detection Accuracy</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>60s</span>
                <span style={styles.statLabel}>Response Time</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <ModernCTA />
    </div>
  );
};

const styles = {
  container: {
    marginTop: '110px',
    color: '#ffffff',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
    lineHeight: 1.6,
    overflowX: 'hidden',
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 5%',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      textAlign: 'left',
      justifyContent: 'space-between',
    },
  },
  heroContent: {
    maxWidth: '100%',
    marginBottom: '40px',
    '@media (min-width: 768px)': {
      maxWidth: '600px',
      marginBottom: 0,
    },
  },
  heroTitle: {
    fontSize: '2.5rem',
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: '20px',
    background: 'linear-gradient(90deg, #ffffff 0%, #cbd5e1 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    '@media (min-width: 768px)': {
      fontSize: '3.5rem',
      marginBottom: '24px',
    },
  },
  heroSubtitle: {
    fontSize: '1.1rem',
    color: '#94a3b8',
    marginBottom: '30px',
    maxWidth: '100%',
    '@media (min-width: 768px)': {
      fontSize: '1.25rem',
      maxWidth: '500px',
      marginBottom: '40px',
    },
  },
  heroVisualization: {
    position: 'relative',
    width: '100%',
    height: '250px',
    maxWidth: '400px',
    '@media (min-width: 768px)': {
      width: '400px',
      height: '300px',
    },
  },
  cameraArray: {
    position: 'absolute',
    top: '0',
    right: '0',
    display: 'flex',
    gap: '15px',
    '@media (min-width: 768px)': {
      gap: '20px',
    },
  },
  camera: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: 'rgba(59, 130, 246, 0.2)',
    border: '2px solid rgba(59, 130, 246, 0.5)',
    position: 'relative',
    '@media (min-width: 768px)': {
      width: '60px',
      height: '60px',
    },
  },
  dataGrid: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    height: '180px',
    background: 'rgba(15, 23, 42, 0.5)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridTemplateRows: 'repeat(4, 1fr)',
    gap: '4px',
    padding: '8px',
    '@media (min-width: 768px)': {
      width: '300px',
      height: '200px',
    },
  },
  capabilities: {
    padding: '60px 5%',
    background: '#0f172a',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  sectionHeader: {
    textAlign: 'center',
    maxWidth: '100%',
    margin: '0 auto 40px',
    '@media (min-width: 768px)': {
      maxWidth: '700px',
      margin: '0 auto 60px',
    },
  },
  sectionTag: {
    display: 'inline-block',
    color: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: '16px',
    lineHeight: 1.3,
    '@media (min-width: 768px)': {
      fontSize: '2.5rem',
    },
  },
  sectionDescription: {
    fontSize: '1rem',
    color: '#94a3b8',
    lineHeight: 1.7,
    '@media (min-width: 768px)': {
      fontSize: '1.125rem',
    },
  },
  capabilityGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (min-width: 600px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '32px',
    },
  },
  capabilityCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '30px',
    '@media (min-width: 768px)': {
      padding: '40px 32px',
    },
  },
  capabilityIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    color: '#3b82f6',
    '@media (min-width: 768px)': {
      width: '56px',
      height: '56px',
      marginBottom: '24px',
    },
  },
  capabilityTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '12px',
    '@media (min-width: 768px)': {
      fontSize: '1.5rem',
      marginBottom: '16px',
    },
  },
  capabilityText: {
    color: '#94a3b8',
    fontSize: '0.9375rem',
    lineHeight: 1.7,
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      fontSize: '1rem',
      marginBottom: '24px',
    },
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  featureListItem: {
    marginBottom: '10px',
    color: '#e2e8f0',
    fontSize: '0.9375rem',
    position: 'relative',
    paddingLeft: '24px',
    '@media (min-width: 768px)': {
      marginBottom: '12px',
      fontSize: '1rem',
    },
  },
  integration: {
    padding: '60px 5%',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  integrationContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    gap: '40px',
    '@media (min-width: 992px)': {
      flexDirection: 'row',
      gap: '60px',
    },
  },
  integrationText: {
    flex: 1,
    width: '100%',
  },
  integrationDescription: {
    fontSize: '1rem',
    color: '#94a3b8',
    margin: '20px 0 30px',
    lineHeight: 1.7,
    '@media (min-width: 768px)': {
      fontSize: '1.125rem',
      margin: '24px 0 32px',
    },
  },
  integrationPoints: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    '@media (min-width: 768px)': {
      gap: '24px',
    },
  },
  integrationPoint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    '@media (min-width: 768px)': {
      gap: '16px',
    },
  },
  pointIndicator: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: '#3b82f6',
    marginTop: '4px',
    flexShrink: 0,
    '@media (min-width: 768px)': {
      width: '16px',
      height: '16px',
    },
  },
  pointTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    margin: '0 0 4px 0',
    color: '#e2e8f0',
    '@media (min-width: 768px)': {
      fontSize: '1.25rem',
    },
  },
  pointText: {
    color: '#94a3b8',
    fontSize: '0.875rem',
    margin: 0,
    lineHeight: 1.6,
    '@media (min-width: 768px)': {
      fontSize: '0.9375rem',
    },
  },
  integrationVisual: {
    flex: 1,
    width: '100%',
  },
  cityGrid: {
    background: '#0f172a',
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    '@media (min-width: 768px)': {
      padding: '16px',
      gap: '8px',
    },
  },
  gridRow: {
    display: 'flex',
    gap: '6px',
    '@media (min-width: 768px)': {
      gap: '8px',
    },
  },
  gridCell: {
    width: '100%',
    height: '40px',
    background: 'rgba(59, 130, 246, 0.1)',
    borderRadius: '4px',
    flex: 1,
    '@media (min-width: 768px)': {
      width: '50px',
      height: '50px',
    },
  },
  gridCellActive: {
    width: '100%',
    height: '40px',
    background: 'rgba(59, 130, 246, 0.3)',
    borderRadius: '4px',
    border: '1px solid #3b82f6',
    flex: 1,
    '@media (min-width: 768px)': {
      width: '50px',
      height: '50px',
    },
  },
  caseStudies: {
    padding: '60px 5%',
    background: '#0f172a',
    '@media (min-width: 768px)': {
      padding: '100px 5%',
    },
  },
  studyGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '32px',
    },
  },
  studyCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '30px',
    '@media (min-width: 768px)': {
      padding: '40px',
    },
  },
  studyHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      marginBottom: '24px',
    },
  },
  studyLogo: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: 'rgba(59, 130, 246, 0.1)',
    marginRight: '12px',
    '@media (min-width: 768px)': {
      width: '48px',
      height: '48px',
      marginRight: '16px',
    },
  },
  studyTitle: {
    fontSize: '1.3rem',
    fontWeight: 600,
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '1.5rem',
    },
  },
  studyText: {
    color: '#94a3b8',
    fontSize: '0.9375rem',
    lineHeight: 1.7,
    marginBottom: '24px',
    '@media (min-width: 768px)': {
      fontSize: '1rem',
      marginBottom: '32px',
    },
  },
  studyStats: {
    display: 'flex',
    gap: '20px',
    '@media (min-width: 768px)': {
      gap: '24px',
    },
  },
  statItem: {
    textAlign: 'center',
  },
  statNumber: {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#3b82f6',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '2rem',
    },
  },
  statLabel: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    '@media (min-width: 768px)': {
      fontSize: '0.875rem',
    },
  },
};

export default AISurveillance;